import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { emptyLocationCopy } from './locationOptions';

/**
 * LocationPicker — the 0 / 1 / many location picker that sits under
 * CalendarPicker on every calendar-hosted scheduling surface (issues #513,
 * #514). Mirrors CalendarPicker's three-state contract:
 *   0 options → explained empty state (`location-empty-state`)
 *   1 option  → fixed bordered box     (`location-fixed`)
 *   many      → Radix Select           (`location-select`)
 *
 * The OPTIONS are the caller's responsibility — build them with
 * `buildLocationOptions(selectedCalendar)` so the provider rule (Google mints
 * Meet, Outlook mints Teams) lives in exactly one place. This component only
 * renders whatever it is given.
 *
 * @param {{value:string, label:string}[]} options   from buildLocationOptions
 * @param {string|undefined} value                    selected option value
 * @param {(value: string) => void} onChange
 * @param {string|undefined} calendarLabel            selected calendar's label, for the empty-state copy
 */
export default function LocationPicker({ options, value, onChange, calendarLabel }) {
    return (
        <div>
            <Label className="text-xs">Location</Label>
            {options.length === 0 ? (
                <p className="mt-1 text-xs text-stone-500 rounded-md border border-dashed border-stone-200 px-3 py-2"
                   data-testid="location-empty-state">
                    {emptyLocationCopy(calendarLabel)}
                </p>
            ) : options.length === 1 ? (
                <div className="mt-1 text-sm rounded-md border border-stone-200 px-3 py-2 bg-stone-50"
                     data-testid="location-fixed">
                    {options[0].label}
                </div>
            ) : (
                // `?? undefined`: Radix's Select treats null as neither a
                // controlled value nor "uncontrolled" (CalendarPicker precedent).
                <Select value={value ?? undefined} onValueChange={onChange}>
                    <SelectTrigger data-testid="location-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}
